import React, { useState, useRef, useEffect, useCallback } from "react";
import { Check, X, ChevronRight, Eye, EyeOff } from "lucide-react";
import { HSK_COLORS } from "./constants";

// ── Answer matching ──────────────────────────────────────────────────
function normalize(s) {
  return (s ?? "").toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Returns: "correct" | "partial" | "wrong"
 * - Correct : exact match OR matches any part in a slash/semicolon-separated list
 * - Partial  : user's answer is a substring (or superset) of correct, min 2 chars
 * - Wrong   : none of the above
 */
function checkAnswer(userInput, correctAnswer) {
  const u = normalize(userInput);
  if (!u) return null;
  const c = normalize(correctAnswer);

  if (u === c) return "correct";

  // Accept any segment from "học tập / học / study" style multi-answers
  const segments = c.split(/[/;，,]/).map((p) => p.trim()).filter(Boolean);
  if (segments.some((seg) => normalize(seg) === u)) return "correct";

  // Partial: non-trivial substring overlap
  if (u.length >= 2 && (c.includes(u) || u.includes(c))) return "partial";

  return "wrong";
}

// ── Component ────────────────────────────────────────────────────────
/**
 * QuizCard — shared component for two quiz modes:
 *   mode="type-meaning"  → show Word + Pinyin, user types Meaning
 *   mode="reverse"       → show Meaning (+ optional example hint), user types Word
 *
 * Props:
 *   card          – vocabulary object
 *   mode          – "type-meaning" | "reverse"
 *   animClass     – CSS animation class
 *   onResult(r)   – called with "correct" | "partial" | "wrong" on submit
 *   onNext()      – called when user clicks Next / presses Enter after result
 */
export default function QuizCard({ card, mode, animClass = "", onResult, onNext }) {
  const [input,    setInput]    = useState("");
  const [result,   setResult]   = useState(null); // null | "correct" | "partial" | "wrong"
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);

  // Reset state whenever card changes
  useEffect(() => {
    setInput("");
    setResult(null);
    setShowHint(false);
    // Delay focus slightly so the slide-in animation doesn't jank
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [card?._id]);

  const isTypeMeaning  = mode === "type-meaning";
  const correctAnswer  = isTypeMeaning ? card.meaning : card.word;
  const isAnswered     = result !== null;
  const hskClass       = HSK_COLORS[card.hskLevel] ?? "bg-gray-100 text-gray-600";

  const submit = useCallback(() => {
    if (!input.trim()) return;
    const r = checkAnswer(input, correctAnswer);
    setResult(r);
    onResult?.(r);
  }, [input, correctAnswer, onResult]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isAnswered) onNext?.();
      else submit();
    }
  };

  // ── Colours ────────────────────────────────────────────────────────
  const borderClass = !isAnswered
    ? "border-gray-200 focus-within:border-blue-400"
    : result === "correct" ? "border-green-400 bg-green-50/60"
    : result === "partial"  ? "border-yellow-400 bg-yellow-50/60"
    : "border-red-400 bg-red-50/60";

  const feedbackClass = result === "correct"
    ? "bg-green-50 text-green-700 border-green-200"
    : result === "partial"
    ? "bg-yellow-50 text-yellow-700 border-yellow-200"
    : "bg-red-50 text-red-700 border-red-200";

  const barClass = !isAnswered
    ? "bg-gradient-to-r from-blue-400 to-blue-600"
    : result === "correct" || result === "partial"
    ? "bg-gradient-to-r from-green-400 to-green-600"
    : "bg-gradient-to-r from-red-400 to-red-600";

  return (
    <div className={`w-full ${animClass}`}>
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        {/* Colour accent bar */}
        <div className={`h-1.5 transition-all duration-500 ${barClass}`} />

        {/* ── Prompt ── */}
        <div className="flex flex-col items-center gap-4 px-10 pt-7 pb-5">
          <span className="text-xs font-bold tracking-widest text-blue-400 uppercase">
            {isTypeMeaning ? "Nhập nghĩa tiếng Việt" : "Nhập từ tiếng Trung"}
          </span>

          {isTypeMeaning ? (
            /* Type-Meaning: show word + pinyin */
            <div className="flex flex-col items-center gap-2">
              <span className="text-5xl font-bold text-gray-900 tracking-wide leading-none">
                {card.word}
              </span>
              {card.pinyin && (
                <span className="text-xl text-red-500 font-medium tracking-wide">
                  {card.pinyin}
                </span>
              )}
              {card.hskLevel && (
                <span className={`text-xs font-bold px-2.5 py-1 rounded-xl ${hskClass}`}>
                  HSK {card.hskLevel}
                </span>
              )}
            </div>
          ) : (
            /* Reverse: show meaning (+ optional example hint) */
            <div className="flex flex-col items-center gap-2 text-center max-w-sm">
              <p className="text-2xl font-bold text-gray-800 leading-snug">{card.meaning}</p>
              {showHint && card.pinyin && (
                <p className="text-base text-red-400 italic animate-fade-up">{card.pinyin}</p>
              )}
              {showHint && card.example && (
                <p className="text-sm text-gray-400 italic animate-fade-up">{card.example}</p>
              )}
              {card.hskLevel && (
                <span className={`text-xs font-bold px-2.5 py-1 rounded-xl ${hskClass}`}>
                  HSK {card.hskLevel}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Input + Actions ── */}
        <div className="px-8 pb-7 flex flex-col gap-3">
          {/* Input */}
          <div className={`flex items-center gap-2 border-2 rounded-2xl px-4 py-3 transition-colors ${borderClass}`}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isAnswered}
              placeholder={isTypeMeaning ? "Nhập nghĩa…" : "Nhập từ tiếng Trung…"}
              className="flex-1 bg-transparent outline-none text-base text-gray-800 placeholder:text-gray-300 disabled:opacity-70"
            />
            {isAnswered && (
              result === "correct" || result === "partial"
                ? <Check size={17} className="text-green-600 shrink-0" />
                : <X size={17} className="text-red-500 shrink-0" />
            )}
          </div>

          {/* Result feedback */}
          {isAnswered && (
            <div className={`rounded-2xl px-4 py-2.5 text-sm font-medium border flex items-start gap-2 animate-fade-up ${feedbackClass}`}>
              {result === "correct" && (
                <><Check size={14} className="mt-0.5 shrink-0" />Chính xác! 🎉</>
              )}
              {result === "partial" && (
                <><Check size={14} className="mt-0.5 shrink-0" />Gần đúng! Đáp án đầy đủ: <strong className="ml-1">{correctAnswer}</strong></>
              )}
              {result === "wrong" && (
                <><X size={14} className="mt-0.5 shrink-0" />Chưa đúng. Đáp án: <strong className="ml-1">{correctAnswer}</strong></>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2">
            {/* Hint button (reverse mode, before answering) */}
            {!isTypeMeaning && !isAnswered && (card.example || card.pinyin) && (
              <button
                onClick={() => setShowHint((h) => !h)}
                className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                {showHint ? <EyeOff size={13} /> : <Eye size={13} />}
                {showHint ? "Ẩn gợi ý" : "Gợi ý"}
              </button>
            )}

            {!isAnswered ? (
              <button
                onClick={submit}
                disabled={!input.trim()}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 active:scale-95 transition-all"
              >
                Kiểm tra
              </button>
            ) : (
              <button
                onClick={onNext}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all"
              >
                Tiếp theo <ChevronRight size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
